const SENSITIVE_FIELD =
  /authorization|cookie|password|secret|signature|(^|_)token($|_)|access_token|refresh_token|api[_-]?key|browser[_-]?id|^code$|short[_-]?code/i
const SENSITIVE_QUERY =
  /([?&#](?:access_token|refresh_token|token|code|signature|secret|api[_-]?key)=)[^&#]*/gi
const PORTAL_SHORT_LINK_PATH = /(\/p\/)[A-Za-z0-9_-]{22}([^A-Za-z0-9_-]|$)/g

const URL_FIELD_NAMES = new Set(['url', 'from', 'to', 'transaction'])

type EnumerableEntry = readonly [key: string, value: unknown]

interface InspectedContainerBase {
  entries: EnumerableEntry[]
}

type InspectedContainer =
  | (InspectedContainerBase & { isArray: true; length: number })
  | (InspectedContainerBase & {
      isArray: false
      prototype: object | null
    })

/**
 * Returns a privacy-scrubbed copy of a Sentry event without mutating browser
 * objects that may be read-only. Only arrays and plain objects are traversed;
 * every other object is treated as an opaque value.
 */
export function scrubSentryEvent<T>(event: T): T {
  return sanitize(event, new WeakMap<object, object>()) as T
}

function sanitize(value: unknown, clones: WeakMap<object, object>): unknown {
  if (value === null || typeof value !== 'object') return value

  const existingClone = clones.get(value)
  if (existingClone !== undefined) return existingClone

  const inspected = inspectContainer(value)
  if (inspected === undefined) return value

  let clone: object
  try {
    clone = inspected.isArray
      ? new Array(inspected.length)
      : Object.create(inspected.prototype)
  } catch {
    return value
  }
  clones.set(value, clone)

  try {
    for (const [key, child] of inspected.entries) {
      const sanitized = SENSITIVE_FIELD.test(key)
        ? '[Filtered]'
        : typeof child === 'string'
          ? sanitizeString(key, child)
          : sanitize(child, clones)

      Object.defineProperty(clone, key, {
        configurable: true,
        enumerable: true,
        value: sanitized,
        writable: true,
      })
    }
  } catch {
    clones.delete(value)
    return value
  }

  return clone
}

/**
 * Redacts known-sensitive query parameters and portal short-link codes from a
 * URL. Used when scrubbing Sentry events, and by anything else that ships a
 * user's current URL off the device — a support report, for instance.
 */
export function redactSensitiveUrl(value: string): string {
  return value
    .replace(SENSITIVE_QUERY, '$1[Filtered]')
    .replace(PORTAL_SHORT_LINK_PATH, '$1[Filtered]$2')
}

function sanitizeString(key: string, value: string): string {
  return URL_FIELD_NAMES.has(key)
    ? redactSensitiveUrl(value)
    : value.replace(PORTAL_SHORT_LINK_PATH, '$1[Filtered]$2')
}

function inspectContainer(value: object): InspectedContainer | undefined {
  try {
    const isArray = Array.isArray(value)
    let prototype: object | null | undefined

    if (!isArray) {
      prototype = Object.getPrototypeOf(value)
      if (prototype !== Object.prototype && prototype !== null) return undefined
    }

    const keys = Object.keys(value)
    const entries: EnumerableEntry[] = []
    for (const key of keys) {
      entries.push([
        key,
        SENSITIVE_FIELD.test(key)
          ? undefined
          : (value as Record<string, unknown>)[key],
      ])
    }

    return isArray
      ? { entries, isArray, length: (value as unknown[]).length }
      : { entries, isArray, prototype: prototype ?? null }
  } catch {
    return undefined
  }
}
